import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import AdminDialog, {AdminDialogProperties, AdminDialogState} from "./AdminDialog";
import AdminDialogType from "../AdminDialogType";
import Message from "../../../shared/log/Message";
import {MessageType} from "../../../shared/log/MessageInterface";
import Administration from "../../session/Administration";
import {
	Application,
	ConfigurationType,
	Portal,
	PortalAuth,
	PortalAuthTimeout,
	ServerInterface,
	SessionAccessToken
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Translate from "../../../shared/translation/Translate";
import Helper from "../../../shared/components/Helper";
import Icon from "../../../shared/components/Icon";
import {InputSwitch} from "primereact/inputswitch";
import PortalAuthDialogFields from "../field/PortalAuthDialogFields";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import Info, {InfoType} from "../../../shared/components/Info";
import TimeUnit from "../../../shared/units/TimeUnit";
import {InputNumber} from "primereact/inputnumber";
import Immutable from "../../../shared/immutable/Immutable";
import MessageBox from "../../../shared/components/MessageBox";

export interface PortalAuthDialogProperties extends AdminDialogProperties<Portal> {
	serverConfig?: Immutable<ServerInterface>;
}

export class PortalAuthDialogDefinition extends AdminDialog<Portal, PortalAuthDialogProperties,
	AdminDialogState<Portal>> {
	public renderContents(): ReactNode {
		let parameter: Portal | undefined = this.state.parameter;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let serverConfig: ServerInterface | undefined = this.props.serverConfig?.get();
		let accessTokenExpiration: number = FunctionHelper.applyValue(
			serverConfig?.localAuth?.rest.accessToken?.expiration, SessionAccessToken.getExpirationDefault()
		);

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor="control-enabled">
							<Translate {...this.props} data="portal_auth.enabled" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={PortalAuth.getEnabledDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-enabled"
										 checked={FunctionHelper.applyValue(
											 parameter?.auth?.enabled, PortalAuth.getEnabledDefault()
										 )}
										 onChange={(e) => this.onChange(PortalAuthDialogFields.ENABLED, e.value)}/>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={!FunctionHelper.applyValue(
						parameter?.auth?.enabled, PortalAuth.getEnabledDefault()
					)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-anonymous">
							<Translate {...this.props} data="portal_auth.anonymous" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={PortalAuth.getAllowAnonymousDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-anonymous"
										 checked={FunctionHelper.applyValue(
											 parameter?.auth?.allowAnonymous, PortalAuth.getAllowAnonymousDefault()
										 )}
										 onChange={(e) => this.onChange(
											 PortalAuthDialogFields.ANONYMOUS, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={!FunctionHelper.applyValue(
						parameter?.auth?.enabled, PortalAuth.getEnabledDefault()
					)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-inactivity">
							<Translate {...this.props} data="portal_auth.inactivity" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={PortalAuthTimeout.getInactivityDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-inactivity" suffix={" " + TimeUnit.SECOND} useGrouping={false}
										 step={1} showButtons={true}
										 value={FunctionHelper.applyValue(
											 parameter?.auth?.timeout?.inactivity,
											 PortalAuthTimeout.getInactivityDefault()
										 )}
										 min={PortalAuthTimeout.getInactivityMin()}
										 onChange={(e) => this.onChange(
											 PortalAuthDialogFields.INACTIVITY, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={!FunctionHelper.applyValue(
						parameter?.auth?.enabled, PortalAuth.getEnabledDefault()
					)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-auto-refresh">
							<Translate {...this.props} data="portal_auth.auto_refresh.label" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={PortalAuthTimeout.getAutoRefreshDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-auto-refresh" suffix={" " + TimeUnit.SECOND}
										 step={1} showButtons={true} useGrouping={false}
										 value={FunctionHelper.applyValue(
											 parameter?.auth?.timeout?.autoRefresh,
											 PortalAuthTimeout.getAutoRefreshDefault()
										 )}
										 min={PortalAuthTimeout.getAutoRefreshMin()}
										 onChange={(e) => this.onChange(
											 PortalAuthDialogFields.AUTO_REFRESH, e.value
										 )}/>
						</div>
					</div>
					<MessageBox type={MessageType.WARNING} language={this.props.language}
									visible={FunctionHelper.applyValue(
										parameter?.auth?.timeout?.autoRefresh,
										PortalAuthTimeout.getAutoRefreshDefault()
									) >= accessTokenExpiration}>
						<Translate {...this.props} data="portal_auth.auto_refresh.limit" namespace="dialog"
								   values={{expiry: accessTokenExpiration + " " + TimeUnit.SECOND}}/>
					</MessageBox>
				</fieldset>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["portal-auth-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.admin.portal_auth.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-shield"}/>
					<Translate {...this.props} data="portal_auth.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected onChange(field: PortalAuthDialogFields, value: any): void {
		let parameter: Portal | undefined = this.state.parameter;

		switch (field) {
			case PortalAuthDialogFields.ENABLED:
				if (!FunctionHelper.exists(parameter?.auth)) {
					parameter!.auth = new PortalAuth({});
				}

				parameter!.auth!.enabled = value;
				break;
			case PortalAuthDialogFields.ANONYMOUS:
				if (!FunctionHelper.exists(parameter?.auth)) {
					parameter!.auth = new PortalAuth({});
				}

				parameter!.auth!.allowAnonymous = value;
				break;
			case PortalAuthDialogFields.INACTIVITY:
				if (!FunctionHelper.exists(parameter?.auth)) {
					parameter!.auth = new PortalAuth({});
				}

				if (!FunctionHelper.exists(parameter?.auth?.timeout)) {
					parameter!.auth!.timeout = new PortalAuthTimeout({});
				}

				parameter!.auth!.timeout!.inactivity = value;
				break;
			case PortalAuthDialogFields.AUTO_REFRESH:
				if (!FunctionHelper.exists(parameter?.auth)) {
					parameter!.auth = new PortalAuth({});
				}

				if (!FunctionHelper.exists(parameter?.auth?.timeout)) {
					parameter!.auth!.timeout = new PortalAuthTimeout({});
				}

				parameter!.auth!.timeout!.autoRefresh = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		}, () => {
			super.onChange(field, value);
		});
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let administration: Administration = this.props.context.session.getAdministration();
		let applicationConfiguration: Application = (
			await administration.getConfiguration(ConfigurationType.Application) as Application
		).clone();
		applicationConfiguration.portal = this.state.parameter!;

		try {
			await administration.updateConfiguration(ConfigurationType.Application, applicationConfiguration);

			this.setState({
				hasChanges: false
			});
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}
			return;
		}

		let message: Message = new Message("success.administration.portal_auth", MessageType.SUCCESS);
		this.props.context.getPage().addMessage(message);
		this.props.context.getPage().setState({
			showRestartMessage: true
		});
		this.hide();
	}

	protected getDialogType(): AdminDialogType {
		return AdminDialogType.PORTAL_AUTH;
	}
}

const PortalAuthDialog = React.memo(PortalAuthDialogDefinition,
	(prevProps: Readonly<PortalAuthDialogProperties>, nextProps: Readonly<PortalAuthDialogProperties>) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default PortalAuthDialog;